'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { cn } from '@/lib/utils'
import { generateDownload } from './cropImage'

// Image cropper component
// TO BE USED ONLY INSIDE OF FORMS

// This component now is used as a wrapper whereby the file input and the image preview are just passed in as children
// EXAMPLE OF INTENDED USEAGE: app/create/forms/community/fields/header-field.js

// This has been built like this to allow for infinite customisation of the UI as well as having structure to be
// used with forms and zod form validation

export default function imageUploaderCrop({
  currentImg,
  setCurrentImg,
  isOpen,
  setIsOpen,
  aspectRatio = 1,
  cropTitle = 'Crop Your Image',
  cropDescription = '3mb max file size. File formats JPG, PNG, GIF.',
  cropSubmitLabel = 'Complete',
  children,
  className,
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const cropAreaPixelsRef = useRef(null)
  const imageUrlRef = useRef(null)
  const imageUrl = useMemo(() => {
    if (!currentImg) {
      return null
    }

    return URL.createObjectURL(currentImg)
  }, [currentImg])

  async function onCropComplete(croppedArea, croppedAreaPixels) {
    cropAreaPixelsRef.current = croppedAreaPixels
  }

  async function saveCroppedImage() {
    if (!imageUrlRef.current || !cropAreaPixelsRef.current) {
      return
    }

    const croppedImgDataUrl = await generateDownload(
      imageUrlRef.current,
      cropAreaPixelsRef.current,
    )

    const file = new File([croppedImgDataUrl], 'fileName.jpg', {
      type: 'image/jpeg',
      lastModified: new Date(),
    })

    setCurrentImg(file)
    setIsOpen(false)
  }

  useEffect(() => {
    if (!imageUrl) {
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current)
        imageUrlRef.current = null
      }
      return
    }

    if (imageUrlRef.current && imageUrlRef.current !== imageUrl) {
      URL.revokeObjectURL(imageUrlRef.current)
    }
    imageUrlRef.current = imageUrl

    return () => {
      URL.revokeObjectURL(imageUrl)
      if (imageUrlRef.current === imageUrl) {
        imageUrlRef.current = null
      }
    }
  }, [imageUrl])

  return (
    <div className={cn('', className)}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen} className="animate-none">
        <DialogContent className="min-h-[320px] w-5/6 p-6 desktop-xs:w-[40%]">
          <DialogHeader>
            <DialogTitle className="px-12 pt-6 text-h-4 font-semibold">
              {cropTitle}
            </DialogTitle>
            <DialogDescription className="px-12 pt-3 text-p-2 font-normal">
              {cropDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="crop-container relative h-auto min-h-[40vh] w-full">
            {imageUrl && (
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            )}
          </div>
          <Button type="button" className="w-full" onClick={saveCroppedImage}>
            {cropSubmitLabel}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  )
}
